// API timestamps without a timezone are UTC; preserve explicit offsets and Date instances.
export const parseUtcDate = (value: string | Date): Date => {
  if (value instanceof Date) return new Date(value.getTime())
  const normalized = value.trim().replace(' ', 'T')
  return new Date(
    /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized) ? normalized : normalized + 'Z'
  )
}

export const getBusinessProfileDate = (
  value: string | Date,
  offset: number
): Date => new Date(parseUtcDate(value).getTime() + offset * 3600000)

export const getDateString = (datetime: Date) => {
  if (!datetime) {
    return ''
  }

  const day = String(datetime.getDate()).padStart(2, '0')
  const month = String(datetime.getMonth() + 1).padStart(2, '0')
  const year = String(datetime.getFullYear())
  return `${year}-${month}-${day}`
}

export const getCombinedDateTime = (
  date: Date | null,
  time: string | null,
  offset: number
): string | null => {
  if (!date || !time) return null

  const [hours, minutes] = time.split(':').map(Number)

  const baseMs = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes
  )

  const targetDate = new Date(baseMs - offset * 60 * 60 * 1000)

  return targetDate.toISOString()
}

export const formatDateTime = (dateTime: string | Date, offset: number) => {
  if (!dateTime) return '-'

  const shiftedDate = getBusinessProfileDate(dateTime, offset)
  if (Number.isNaN(shiftedDate.getTime())) return '-'

  const d = shiftedDate.getUTCDate().toString().padStart(2, '0')
  const m = (shiftedDate.getUTCMonth() + 1).toString().padStart(2, '0')
  const y = shiftedDate.getUTCFullYear()

  let h = shiftedDate.getUTCHours()
  const min = shiftedDate.getUTCMinutes().toString().padStart(2, '0')
  const ampm = h >= 12 ? 'PM' : 'AM'

  h = h % 12
  h = h ? h : 12

  const hStr = h.toString().padStart(2, '0')

  return `${d}/${m}/${y} ${hStr}:${min} ${ampm}`
}

export const extractLocalDateAndTime = (
  dateTime: string | null,
  offset: number
): { date: Date | null; time: string } => {
  if (!dateTime) return { date: null, time: '00:00' }

  const shiftedDate = getBusinessProfileDate(dateTime, offset)
  const year = shiftedDate.getUTCFullYear()
  const month = shiftedDate.getUTCMonth()
  const day = shiftedDate.getUTCDate()
  const h = shiftedDate.getUTCHours().toString().padStart(2, '0')
  const m = shiftedDate.getUTCMinutes().toString().padStart(2, '0')
  const localDate = new Date(year, month, day)
  return {
    date: localDate,
    time: `${h}:${m}`
  }
}

export const getNowInZone = (offset: number) => {
  const offsetMs = offset * 60 * 60 * 1000
  return new Date(Date.now() + offsetMs)
}

export const getNowBusinessDateString = (offset: number) => {
  const d = getNowInZone(offset)

  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')

  return `${y}-${m}-${day}`
}

export const getNowBusinessTimeString = (offset: number) => {
  const d = getNowInZone(offset)

  const h = String(d.getUTCHours()).padStart(2, '0')
  const m = String(d.getUTCMinutes()).padStart(2, '0')

  return `${h}:${m}`
}

export const formatLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
